const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Types
export interface Notification {
  _id: string;
  name: string;
  description: string;
  type: 'Game' | 'Comment' | 'Other';
  url?: string;
  viewed: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface NotificationPreferences {
  emailNotificationsEnabled?: boolean;
  emailNotificationsForComments?: boolean;
  emailNotificationsForNewGames?: boolean;
  emailNotificationsForNewTournaments?: boolean;
  emailNotificationsForRoundPairings?: boolean;
}

export interface User {
  _id: string;
  email: string;
  displayName: string;
  avatar: string;
  realName?: string;
  discordName?: string;
  mahjongSoulName?: string;
  favoriteYaku?: string | null;
  favoriteTile?: Tile | null;
  clubAffiliation?: 'Charleston' | 'Charlotte' | 'Washington D.C.';
  notifications?: Notification[];
  notificationPreferences?: NotificationPreferences;
  isAdmin?: boolean;
  privateMode?: boolean;
  isGuest?: boolean;
  riichiMusic?: {
    url: string;
    type: 'youtube' | 'spotify';
  } | null;
  pointsBalance?: number;
  totalPointsEarned?: number;
  equippedFlair?: EquippedFlair;
  showcase?: ShowcaseEntry[];
  purchasedItems?: PurchasedItem[];
}

export interface GamePlayer {
  player: User;
  score: number;
  position: number;
  rank?: number;
}

export interface GameComment {
  _id?: string;
  comment: string;
  commenter: User;
  createdAt: string;
}

export interface Game {
  _id: string;
  submittedBy: User;
  players: GamePlayer[];
  gameDate: string;
  notes?: string;
  pointsLeftOnTable?: number;
  isEastOnly?: boolean;
  isRanked?: boolean;
  verified: boolean;
  verifiedBy?: User;
  verifiedAt?: string;
  comments?: GameComment[];
  createdAt: string;
  updatedAt: string;
  tournament?: { _id: string; name: string } | null;
  playerUma?: Array<{ playerId: string; uma: number }> | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items: T[];
    pagination: PaginationData;
  };
}

export interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface UserStats {
  gamesWon: number;
  gamesVerified: number;
  gamesSubmitted: number;
  gamesPlayed: number;
  averageScore: number;
  highestScore: number;
  lowestScore: number;
  quizzesRespondedTo: number;
  commentsMade: number;
}

export interface DiscardQuiz {
  id: string;
  hand: Tile[];
  doraIndicator: Tile;
  seat: 'E' | 'S' | 'W' | 'N';
  roundWind: 'E' | 'S';
  responses: Record<string, string[]>;
  createdAt?: string;
  updatedAt?: string;
}

export interface DecisionQuizMeld {
  tiles: Tile[];
  stolenTileIndex: number | null;
  stolenFromSeat: 'E' | 'S' | 'W' | 'N' | null;
}

export interface DecisionQuizPlayer {
  hand: Tile[];
  discard: Tile[];
  melds: DecisionQuizMeld[];
  seat: 'E' | 'S' | 'W' | 'N';
  isUser: boolean;
  score: number;
  riichiTile: number | null;
}

export interface DecisionQuiz {
  id: string;
  players: DecisionQuizPlayer[];
  doraIndicators: Tile[];
  roundWind: 'E' | 'S';
  roundNumber: number;
  remainingTileCount: number;
  responses: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface Tile {
  _id: string;
  id: string;
  name: string;
  suit: 'Man' | 'Sou' | 'Pin' | 'Wind' | 'Dragon';
}

export interface TournamentPlayer {
  player: User;
  uma: number;
  finalsUma?: number;
  dropped: boolean;
}

export interface TournamentAddress {
  streetAddress: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface TournamentWaitlistEntry {
  player: User | string;
  addedAt?: string;
}

export interface Tournament {
  _id: string;
  name: string;
  description?: string;
  winnerTitle?: string;
  date: string;
  isOnline?: boolean;
  location?: TournamentAddress;
  onlineLocation?: string;
  isEastOnly: boolean;
  ruleset: 'WRC2025';
  createdBy?: string | { _id: string; displayName?: string; avatar?: string };
  status: 'NotStarted' | 'InProgress' | 'Completed' | 'Cancelled';
  players: TournamentPlayer[];
  rounds?: any[];
  modifications?: string[];
  top4?: (string | { _id?: string })[];
  maxPlayers?: number | null;
  roundDurationMinutes?: number | null;
  startingPointValue?: 25000 | 30000;
  roundStrategy?: 'Scramble' | 'TieredPointsOnly' | 'TieredPointsTop4';
  waitlist?: TournamentWaitlistEntry[];
  umaPenalties?: { amount: number; player: string; description: string }[];
  createdAt?: string;
  updatedAt?: string;
}

export interface RankedLeaguePlayer {
  player: User;
  rankedPoints: number;
  gamesPlayed: number;
}

export interface RankedLeague {
  _id: string;
  startDate: string;
  rankedGamesThreshold: number;
  players: RankedLeaguePlayer[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Returns display label for a round (e.g. "Round 3" or "Finals 1 of 2").
 */
/** Finals count: TieredPointsOnly = 0, Scramble and TieredPointsTop4 = 2 */
function getNumberOfFinals(roundStrategy?: string): number {
  if (roundStrategy === 'TieredPointsOnly') return 0;
  return 2;
}

export function getRoundLabel(roundNumber: number, tournament: Tournament): string {
  const activeCount = tournament.players?.filter((p) => !p.dropped).length ?? 0;
  const maxRounds = Math.ceil(((activeCount - 4) / 12) + 1);
  const numberOfFinals = getNumberOfFinals(tournament.roundStrategy);
  if (numberOfFinals > 0 && roundNumber > maxRounds) {
    const finalsIndex = roundNumber - maxRounds;
    return `Finals ${finalsIndex} of ${numberOfFinals}`;
  }
  return `Round ${roundNumber}`;
}

// Yaku list matching server-side enum
export const YAKU_LIST = [
  'Riichi',
  'All Simples',
  'Fully Concealed Hand',
  'Yakuhai Seat Wind',
  'Yakuhai Prevalent Wind',
  'Yakuhai Dragons',
  'Pinfu',
  'Pure Double Sequence',
  'Robbing a Kan',
  'After a Kan',
  'Under the Sea',
  'Under the River',
  'Ippatsu',
  'Double Riichi',
  'Mixed Triple Triplets',
  'Three Quads',
  'All Triplets',
  'Three Concealed Triplets',
  'Little Three Dragons',
  'All Terminals and Honors',
  'Seven Pairs',
  'Half Outside Hand',
  'Pure Straight',
  'Mixed Triple Sequence',
  'Half Flush',
  'Twice Pure Double Sequence',
  'Full Outside Hand',
  'Mangan at Draw',
  'Full Flush',
  'Counted Yakuman',
  'All Terminals',
  'Thirteen Orphans',
  'Little Four Winds',
  'Four Quads',
  'Nine Gates',
  'Blessing of Heaven',
  'Blessing of Earth',
  'Big Three Dragons',
  'Four Concealed Triplets',
  'All Honors',
  'All Green',
  'Four Concealed Triplets Single Wait',
  'Thirteen Orphans 13-Way Wait',
  'True Nine Gates',
  'Big Four Winds',
];

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Registered by AuthContext so a 401 from any call can clear the stale session.
// Kept out of AuthContext's own import graph (AuthContext imports this file) by
// having AuthContext push a callback in here instead of this file reaching back out.
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void): void => {
  onUnauthorized = handler;
};

// Helper function to make API requests
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getAuthToken();

  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    if (response.status === 401) {
      onUnauthorized?.();
      throw new Error('Unauthorized');
    }

    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Authentication API
export const authApi = {
  register: async (userData: {
    email: string;
    displayName: string;
    password: string;
    realName?: string;
    discordName?: string;
    mahjongSoulName?: string;
    clubAffiliation?: 'Charleston' | 'Charlotte' | 'Washington D.C.';
  }) => {
    return apiRequest<ApiResponse<{ user: User; token: string }>>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (credentials: { email: string; password: string }) => {
    return apiRequest<ApiResponse<{ user: User; token: string }>>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  refreshToken: async () => {
    return apiRequest<ApiResponse<{ token: string }>>('/auth/refresh-token', {
      method: 'POST',
    });
  },

  forgotPassword: async (email: string) => {
    return apiRequest<ApiResponse<{ message: string }>>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPassword: async (token: string, password: string) => {
    return apiRequest<ApiResponse<{ user: User; token: string }>>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },
};

// Users API
export const usersApi = {
  getUsers: async (page = 1, limit = 20) => {
    return apiRequest<PaginatedResponse<User>>(`/users?page=${page}&limit=${limit}`);
  },

  getProfile: async () => {
    return apiRequest<ApiResponse<{ user: User }>>('/users/profile');
  },

  updateProfile: async (userData: Partial<User>) => {
    return apiRequest<ApiResponse<{ user: User }>>('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  getUser: async (userId: string) => {
    return apiRequest<ApiResponse<{ user: User }>>(`/users/${userId}`);
  },

  getUserGames: async (userId: string, page = 1, limit = 10) => {
    return apiRequest<PaginatedResponse<Game>>(`/users/${userId}/games?page=${page}&limit=${limit}`);
  },

  getUserStats: async (userId: string) => {
    return apiRequest<ApiResponse<{ stats: UserStats }>>(`/users/${userId}/stats`);
  },

  searchUsers: async (query: string, limit = 10) => {
    return apiRequest<ApiResponse<{ users: User[] }>>(`/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  },

  updateNotificationPreferences: async (preferences: NotificationPreferences) => {
    return apiRequest<ApiResponse<{ user: User }>>('/users/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  },
};

// Notifications API
export const notificationsApi = {
  getNotifications: async () => {
    return apiRequest<ApiResponse<{ notifications: Notification[] }>>('/users/notifications');
  },

  markAsViewed: async (notificationId: string) => {
    return apiRequest<ApiResponse<{ user: User }>>(`/users/notifications/${notificationId}/view`, {
      method: 'PUT',
    });
  },

  markAllAsViewed: async () => {
    return apiRequest<ApiResponse<{ user: User }>>('/users/notifications/view-all', {
      method: 'PUT',
    });
  },

  deleteNotification: async (notificationId: string) => {
    return apiRequest<ApiResponse<{ user: User }>>(`/users/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  },

  clearAllNotifications: async () => {
    return apiRequest<ApiResponse<{ user: User }>>('/users/notifications', {
      method: 'DELETE',
    });
  },
};

export type GameFilter = 'all' | 'ranked' | 'tournament' | 'normal';

// Games API
export const gamesApi = {
  getGames: async (page = 1, limit = 20, filter: GameFilter = 'all') => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filter && filter !== 'all') params.set('filter', filter);
    return apiRequest<PaginatedResponse<Game>>(`/games?${params.toString()}`);
  },

  createGame: async (gameData: {
    players: Array<{ player: string; score: number; position: number }>;
    gameDate?: Date;
    notes?: string;
    pointsLeftOnTable?: number;
    isEastOnly?: boolean;
    isInPerson?: boolean;
    ranOutOfTime?: boolean;
    isRanked?: boolean;
  }) => {
    return apiRequest<ApiResponse<{ game: Game }>>('/games', {
      method: 'POST',
      body: JSON.stringify(gameData),
    });
  },

  getGame: async (gameId: string) => {
    return apiRequest<ApiResponse<{ game: Game }>>(`/games/${gameId}`);
  },

  verifyGame: async (gameId: string) => {
    return apiRequest<ApiResponse<{ game: Game }>>(`/games/${gameId}/verify`, {
      method: 'PUT',
    });
  },

  updateGameScores: async (
    gameId: string,
    players: Array<{ player: string; score: number; position: number }>
  ) => {
    return apiRequest<ApiResponse<{ game: Game }>>(`/games/${gameId}`, {
      method: 'PATCH',
      body: JSON.stringify({ players }),
    });
  },

  deleteGame: async (gameId: string) => {
    return apiRequest<ApiResponse<null>>(`/games/${gameId}`, {
      method: 'DELETE',
    });
  },

  addComment: async (gameId: string, comment: string) => {
    return apiRequest<ApiResponse<{ game: Game }>>(`/games/${gameId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  },
};

// Tiles API
export const tilesApi = {
  getTiles: async () => {
    return apiRequest<ApiResponse<{ tiles: Tile[] }>>('/tiles');
  },
};

// Discard Quizzes API
export const discardQuizzesApi = {
  getQuiz: async (quizId: string) => {
    return apiRequest<ApiResponse<{ quiz: DiscardQuiz }>>(`/discard-quizzes/${quizId}`);
  },

  generateRandomQuiz: async () => {
    return apiRequest<ApiResponse<{ quiz: DiscardQuiz }>>('/discard-quizzes/generate/random');
  },

  submitResponse: async (quizId: string, tileId: string) => {
    return apiRequest<ApiResponse<{ quiz: DiscardQuiz }>>(`/discard-quizzes/${quizId}/response`, {
      method: 'PUT',
      body: JSON.stringify({ tileId }),
    });
  },
};

export const decisionQuizzesApi = {
  getQuiz: async (quizId: string) => {
    return apiRequest<ApiResponse<{ quiz: DecisionQuiz }>>(`/decision-quizzes/${quizId}`);
  },

  generateRandomQuiz: async () => {
    return apiRequest<ApiResponse<{ quiz: DecisionQuiz }>>('/decision-quizzes/generate/random');
  },

  submitResponse: async (quizId: string, tileId: string, responseData?: any) => {
    return apiRequest<ApiResponse<{ quiz: DecisionQuiz }>>(`/decision-quizzes/${quizId}/response`, {
      method: 'PUT',
      body: JSON.stringify({ tileId, responseData }),
    });
  },
};

// Tournaments API
export const tournamentsApi = {
  getTournaments: async (page = 1, limit = 20) => {
    return apiRequest<PaginatedResponse<Tournament>>(`/tournaments?page=${page}&limit=${limit}`);
  },

  getTournament: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}`);
  },

  getTournamentPublic: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/public/${tournamentId}`);
  },

  createTournament: async (tournamentData: {
    name: string;
    description?: string;
    winnerTitle?: string;
    date: Date;
    isOnline?: boolean;
    location?: TournamentAddress;
    onlineLocation?: string;
    modifications?: string[];
    ruleset?: 'WRC2025' | 'MahjongSoul';
    maxPlayers?: number;
    roundDurationMinutes?: number;
    startingPointValue?: 25000 | 30000;
    roundStrategy?: 'Scramble' | 'TieredPointsOnly' | 'TieredPointsTop4';
  }) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>('/tournaments', {
      method: 'POST',
      body: JSON.stringify(tournamentData),
    });
  },

  updateTournament: async (tournamentId: string, tournamentData: {
    name?: string;
    description?: string;
    winnerTitle?: string;
    date?: Date;
    isOnline?: boolean;
    location?: TournamentAddress;
    onlineLocation?: string;
    modifications?: string[];
    ruleset?: 'WRC2025';
    maxPlayers?: number | null;
    roundDurationMinutes?: number | null;
    startingPointValue?: 25000 | 30000;
    roundStrategy?: 'Scramble' | 'TieredPointsOnly' | 'TieredPointsTop4';
    notifyParticipants?: boolean;
  }) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}`, {
      method: 'PUT',
      body: JSON.stringify(tournamentData),
    });
  },

  signup: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/signup`, {
      method: 'POST',
    });
  },

  drop: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/drop`, {
      method: 'PUT',
    });
  },

  dropFromWaitlist: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/waitlist/drop`, {
      method: 'PUT',
    });
  },

  startTournament: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/start`, {
      method: 'PUT',
    });
  },

  endRound: async (tournamentId: string, roundNumber: number) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/rounds/${roundNumber}/end`, {
      method: 'PUT',
    });
  },

  startRound: async (tournamentId: string, roundNumber: number) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/rounds/${roundNumber}/start`, {
      method: 'PUT',
    });
  },

  reconcileActiveRound: async (tournamentId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/reconcile-active-round`, {
      method: 'PUT',
    });
  },

  addPlayer: async (tournamentId: string, playerId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/players`, {
      method: 'POST',
      body: JSON.stringify({ playerId }),
    });
  },

  kickPlayer: async (tournamentId: string, playerId: string) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(`/tournaments/${tournamentId}/players/${playerId}/kick`, {
      method: 'PUT',
    });
  },

  applyUmaPenalty: async (
    tournamentId: string,
    playerId: string,
    payload: { amount: number; description?: string }
  ) => {
    return apiRequest<ApiResponse<{ tournament: Tournament }>>(
      `/tournaments/${tournamentId}/players/${playerId}/uma-penalty`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  },

  submitTournamentGame: async (
    tournamentId: string,
    gameData: {
      players: Array<{ player: string; score: number; position: number }>;
      notes?: string;
      pointsLeftOnTable?: number;
      ranOutOfTime?: boolean;
      roundNumber: number;
      pairingIndex?: number;
    }
  ) => {
    return apiRequest<ApiResponse<{ game: Game; tournament: Tournament }>>(
      `/tournaments/${tournamentId}/games`,
      {
        method: 'POST',
        body: JSON.stringify(gameData),
      }
    );
  },
};

// Ranked Leagues API
export const rankedLeaguesApi = {
  getCurrent: async () => {
    return apiRequest<ApiResponse<{ league: RankedLeague }>>('/ranked-leagues/current');
  },

  joinLeague: async () => {
    return apiRequest<ApiResponse<{ league: RankedLeague }>>('/ranked-leagues/current/join', {
      method: 'POST',
    });
  },
};

// Points API
export interface PointTransactionContext {
  kind: 'game' | 'tournament' | 'rankedSeason' | 'shopItem' | 'adjustment';
  id: string | null;
  label: string | null;
  missing: boolean;
}

export interface PointTransaction {
  _id: string;
  user: string;
  type: string;
  amount: number;
  metadata: {
    itemId?: string | null;
    gameId?: string | null;
    tournamentId?: string | null;
    leagueId?: string | null;
    placement?: number | null;
    adjustedBy?: string | null;
    reason?: string | null;
  };
  // Present on history rows; absent on the summary's recentTransactions.
  context?: PointTransactionContext | null;
  createdAt: string;
}

export interface PointsSummary {
  balance: number;
  totalEarned: number;
  recentTransactions: PointTransaction[];
}

export interface PointsHistory {
  items: PointTransaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PointsConfig {
  gamePlacementAmounts: { 1: number; 2: number; 3: number; 4: number };
  gameSubmittedAmount: number;
  gameVerifiedAmount: number;
  gameDailyCap: number;
  gameDailyWindowHours: number;
  repeatGroupMaxGames: number;
  repeatGroupWindowDays: number;
  tournamentParticipationAmount: number;
  tournamentPlacementAmounts: number[];
  rankedQualificationAmount: number;
  rankedPlacementAmounts: number[];
  quizCompletionAmount: number;
  quizWeeklyCapCount: number;
  weeklyStreakAmounts: number[];
}

export const pointsApi = {
  getSummary: async () => {
    return apiRequest<ApiResponse<PointsSummary>>('/points/me');
  },

  getHistory: async (page = 1, limit = 20) => {
    return apiRequest<ApiResponse<PointsHistory>>(`/points/me/history?page=${page}&limit=${limit}`);
  },

  getConfig: async () => {
    return apiRequest<ApiResponse<PointsConfig>>('/points/config');
  },

  adjustBalance: async ({ userId, amount, reason }: { userId: string; amount: number; reason: string }) => {
    return apiRequest<ApiResponse<{ message: string }>>('/points/admin/adjust', {
      method: 'POST',
      body: JSON.stringify({ userId, amount, reason }),
    });
  },
};

export type FlairCategory = 'nameColor' | 'nameIcon' | 'profileBorder' | 'profileBackdrop' | 'title';

export interface ShopItem {
  _id: string;
  name: string;
  description: string;
  category: FlairCategory;
  cost: number;
  value: string;
  tier: 'entry' | 'mid' | 'premium' | 'prestige';
  previewCss?: string | null;
  sortOrder: number;
  isActive: boolean;
  acquisition?: 'shop' | 'earned';
  availableUntil?: string | null;
}

// Each slot holds the equipped item's `value` (a CSS class or display text), not its `_id`.
// `profileBackdrop` is profile-only: player payloads in game rows and member lists omit it, and
// a private-mode profile returns it as null.
export interface EquippedFlair {
  nameColor: string | null;
  nameIcon: string | null;
  profileBorder: string | null;
  profileBackdrop?: string | null;
  title: string | null;
}

// A pinned profile showcase entry. `flair` entries carry the item's category and value;
// `stat` entries carry a key of UserStats; favorites resolve from the user's own fields.
export type ShowcaseEntry =
  | { type: 'flair'; category: FlairCategory; value: string }
  | { type: 'favoriteYaku' }
  | { type: 'favoriteTile' }
  | { type: 'stat'; key: ShowcaseStatKey };

// Mirrors SHOWCASE_STAT_KEYS in server/src/utils/showcaseService.js.
export type ShowcaseStatKey = 'gamesWon' | 'gamesPlayed' | 'highestScore' | 'averageScore';

export interface PurchasedItem {
  item: ShopItem;
  purchasedAt: string;
}

// Same explicit-fields shape as EquippedFlair (rather than a mapped type over FlairCategory), since
// a saved loadout is never subject to the profile-privacy filtering that makes EquippedFlair's
// profileBackdrop field optional — every slot is always present here.
export interface FlairLoadout {
  _id: string;
  name: string;
  nameColor: string | null;
  nameIcon: string | null;
  profileBorder: string | null;
  profileBackdrop: string | null;
  title: string | null;
}

export type ShopCatalog = { [category in FlairCategory]?: ShopItem[] };

export interface ShopInventory {
  purchasedItems: PurchasedItem[];
  equippedFlair: EquippedFlair;
  pointsBalance: number;
  flairLoadouts: FlairLoadout[];
}

export const shopApi = {
  getCatalog: async () => {
    return apiRequest<ApiResponse<ShopCatalog>>('/shop');
  },

  getInventory: async () => {
    return apiRequest<ApiResponse<ShopInventory>>('/shop/inventory');
  },

  purchase: async (itemId: string) => {
    return apiRequest<ApiResponse<null>>('/shop/purchase', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  },

  equip: async (itemId: string | null, slot: FlairCategory) => {
    return apiRequest<ApiResponse<null>>('/shop/equip', {
      method: 'POST',
      body: JSON.stringify({ itemId, slot }),
    });
  },

  createLoadout: async (loadout: { name: string } & EquippedFlair) => {
    return apiRequest<ApiResponse<FlairLoadout>>('/shop/loadouts', {
      method: 'POST',
      body: JSON.stringify(loadout),
    });
  },

  renameLoadout: async (loadoutId: string, name: string) => {
    return apiRequest<ApiResponse<FlairLoadout>>(`/shop/loadouts/${loadoutId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  deleteLoadout: async (loadoutId: string) => {
    return apiRequest<ApiResponse<null>>(`/shop/loadouts/${loadoutId}`, {
      method: 'DELETE',
    });
  },

  applyLoadout: async (loadoutId: string) => {
    return apiRequest<ApiResponse<null>>(`/shop/loadouts/${loadoutId}/apply`, {
      method: 'POST',
    });
  },

  seed: async () => {
    return apiRequest<ApiResponse<{ count: number }>>('/shop/seed', {
      method: 'POST',
    });
  },
};

// Health check
export const healthApi = {
  check: async () => {
    return apiRequest<{ status: string; timestamp: string; uptime: number }>('/health');
  },
};

